import React, { ReactNode, useEffect, useRef, useState } from "react";
import {
  ThemeProvider as AmplifyThemeProvider,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import { Authenticator } from "@aws-amplify/ui-react";
import { Alert, Box } from "@mui/material";
import {
  associateWebAuthnCredential,
  confirmResetPassword,
  ConfirmResetPasswordInput,
  signIn,
  SignInInput,
  signUp,
  SignUpInput,
} from "aws-amplify/auth";
import { useDispatch } from "../../store";
import { userSignUp } from "../../slices/auth";
import amplifyTheme from "../../amplifyTheme";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { setSavedRoute } from "../../slices/shared";
import LoginHeader from "../base/LoginHeader";
import { usePostLoginRouting } from "../../hooks/usePostLoginRouting";

interface Props {
  children: ReactNode;
}

const AuthView: React.FC<Props> = (props) => {
  const { children } = props;

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email"));
  const [newCompany, setNewCompany] = useState(
    location.pathname.startsWith("/start")
  );
  const [invite, setInvite] = useState(location.pathname.startsWith("/invite"));

  const hasFetchedUser = useRef(false);
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);

  useEffect(() => {
    setEmail(searchParams.get("email"));
  }, [searchParams]);

  useEffect(() => {
    setNewCompany(location.pathname.startsWith("/start"));
  }, [location.pathname, email]);

  useEffect(() => {
    setInvite(location.pathname.startsWith("/invite"));
  }, [location.pathname, email]);

  useEffect(() => {
    const match = location.pathname.match(/^\/app\/(.+)$/);
    if (match) {
      const sliced = match.slice(1).join("/");
      dispatch(setSavedRoute(sliced || undefined));
    }
  }, [authStatus, location.pathname, dispatch]);

  useEffect(() => {
    if (authStatus === "authenticated") {
      const stored = sessionStorage.getItem("savedRoute");
      if (stored) {
        dispatch(setSavedRoute(stored));
      }
    }
  }, [authStatus, dispatch]);

  useEffect(() => {
    if (authStatus === "authenticated" && !hasFetchedUser.current) {
      hasFetchedUser.current = true;
    } else if (authStatus !== "authenticated") {
      hasFetchedUser.current = false;
    }
  }, [authStatus]);

  useEffect(() => {
    if (authStatus === "unauthenticated" && !email && !newCompany && !invite) {
      navigate("/auth/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, email, newCompany, invite]);

  useEffect(() => {
    if (authStatus === "authenticated" && newCompany) {
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, newCompany]);

  const generateGUID = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
      const rand = (Math.random() * 16) | 0;
      const value = char === "x" ? rand : (rand & 0x3) | 0x8;
      return value.toString(16);
    });
  };

  const components = {
    Header() {
      return <LoginHeader newCompany={newCompany} />;
    },
    Footer() {
      return <Box margin={5} />;
    },
    SignUp: {
      Footer() {
        return (
          <Alert
            severity="info"
            sx={{ alignItems: "center", justifyContent: "center" }}
          >
            You will be directed to create a passkey after signing up. The
            password set here is for backup only.
          </Alert>
        );
      },
    },
  };

  const formFields =
    email || newCompany
      ? {
          signIn: {
            username: {
              label: "Email",
              placeholder: "Enter Email",
              defaultValue: email ?? "",
            },
            password: {
              placeholder: "Enter Password",
            },
          },
          signUp: {
            email: {
              label: "Email",
              placeholder: "Enter Email",
            },
            password: {
              placeholder: "Enter Password",
            },
            confirm_password: {
              placeholder: "Confirm Password",
            },
            given_name: {
              label: "First Name",
              placeholder: "Enter First Name",
            },
            family_name: {
              label: "Last Name",
              placeholder: "Enter Last Name",
            },
          },
          forgotPassword: {
            username: {
              defaultValue: email ?? "",
              placeholder: "Enter Email",
              label: "Email",
            },
          },
          confirmSignIn: {
            confirmation_code: {
              label: "Verification Code",
              placeholder: "Enter Code from Authenticator",
            },
          },
        }
      : undefined;

  const services = {
    async handleForgotPasswordSubmit(input: ConfirmResetPasswordInput) {
      await confirmResetPassword(input);
      await signIn({
        username: input.username,
        password: input.newPassword,
      });
    },
    async handleSignIn(input: SignInInput) {
      const signInInput: SignInInput = {
        ...input,
        options: {
          ...input.options,
          autoSignIn: { enabled: true },
        },
      };

      let res = await signIn(signInInput);

      if (!res.nextStep || res.nextStep.signInStep === "DONE") {
        try {
          await associateWebAuthnCredential();
        } catch (error) {}
      }

      return res;
    },
    async handleSignUp(input: SignUpInput) {
      const guid = generateGUID();
      const userAttributes = {
        ...input.options?.userAttributes,
        preferred_username: guid,
      };
      const signUpInput: SignUpInput = {
        ...input,
        options: {
          ...input.options,
          userAttributes,
          autoSignIn: {
            enabled: true,
          },
        },
      };
      return signUp(signUpInput).then((result) => {
        if (result.userId) {
          dispatch(userSignUp(signUpInput));
        }
        return result;
      });
    },
  };

  usePostLoginRouting(authStatus === "authenticated");

  return (
    <AmplifyThemeProvider theme={amplifyTheme}>
      {(newCompany || authStatus === "authenticated") && (
        <Authenticator
          hideSignUp={!newCompany}
          initialState={!!newCompany ? "signUp" : "signIn"}
          signUpAttributes={["given_name", "family_name"]}
          components={components}
          formFields={formFields}
          services={services}
        >
          {children}
        </Authenticator>
      )}
    </AmplifyThemeProvider>
  );
};

export default AuthView;
